import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { HERO_BG } from "@/lib/content";

const EASE = [0.76, 0, 0.24, 1];

const LINE_VARIANTS = {
  hidden: { y: "110%", opacity: 0 },
  visible: (i) => ({
    y: "0%",
    opacity: 1,
    transition: { duration: 1.1, ease: EASE, delay: 0.35 + i * 0.15 },
  }),
};

function Line({ children, i, testId }) {
  return (
    <span className="line-mask">
      <motion.span
        variants={LINE_VARIANTS}
        initial="hidden"
        animate="visible"
        custom={i}
        data-testid={testId}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.2]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-8%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.35, 0.8]);

  const scrollToQuote = () => {
    document.querySelector("#quote")?.scrollIntoView({ behavior: "smooth" });
  };
  const scrollToWork = () => {
    document.querySelector("#work")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="top"
      ref={ref}
      className="relative min-h-[100svh] w-full overflow-hidden flex flex-col justify-end pt-24 pb-16 md:pb-20"
      style={{ isolation: "isolate" }}
      data-testid="hero-section"
    >
      {/* Parallax background */}
      <motion.div
        className="absolute inset-0 z-0"
        style={{ y: bgY, scale: bgScale }}
      >
        <img
          src={HERO_BG}
          alt="Trilogy Earthworks Kobelco excavator on a Central Queensland site"
          className="w-full h-full object-cover"
          loading="eager"
        />
      </motion.div>
      <motion.div
        className="absolute inset-0 z-[1] bg-black"
        style={{ opacity: overlayOpacity }}
      />
      <div className="absolute inset-0 z-[1] bg-gradient-to-b from-black/40 via-black/10 to-[color:var(--te-bg)]" />

      {/* Right side rotated marker */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.8 }}
        className="hidden lg:block absolute right-10 top-1/2 -translate-y-1/2 origin-center rotate-90 z-10"
      >
        <span className="text-[11px] uppercase tracking-[0.4em] text-white/60 font-display">
          Earthworks · Civil · Bulk
        </span>
      </motion.div>

      {/* Main content */}
      <motion.div
        style={{ y: textY }}
        className="relative z-10 max-w-[1440px] mx-auto w-full px-5 md:px-10"
      >
        <div className="max-w-[1200px]">
          <h1
            className="font-display font-black uppercase tracking-[-0.03em] leading-[0.86] text-white"
            style={{ fontSize: "clamp(3.2rem, 11vw, 11rem)" }}
            data-testid="hero-heading"
          >
            <Line i={0} testId="hero-line-1">Kerb.</Line>
            <Line i={1} testId="hero-line-2">
              <span className="font-serif-italic text-[color:var(--te-primary)] tracking-normal">Stormwater.</span>
            </Line>
            <Line i={2} testId="hero-line-3">Concrete.</Line>
          </h1>

          <div className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1, duration: 0.9, ease: EASE }}
              className="md:col-span-6 lg:col-span-5 text-base md:text-lg text-white/75 font-body leading-relaxed"
              data-testid="hero-subtext"
            >
              Trilogy Earthworks is a Central Queensland civil crew leading with kerb &amp; channel, stormwater management and concrete — backed by full-service earthmoving across Rockhampton and beyond.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.25, duration: 0.9, ease: EASE }}
              className="md:col-span-6 lg:col-span-7 flex flex-col sm:flex-row gap-3 md:justify-end"
            >
              <button onClick={scrollToQuote} className="te-btn te-btn-primary" data-testid="hero-cta-quote">
                Request a Quote →
              </button>
              <button onClick={scrollToWork} className="te-btn te-btn-ghost" data-testid="hero-cta-work">
                See our work
              </button>
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.7, duration: 0.6 }}
        className="absolute bottom-6 right-5 md:right-10 z-10 flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-white/60 font-display"
        data-testid="hero-scroll-cue"
      >
        <span>Scroll</span>
        <span className="block w-10 h-[1px] bg-white/40 relative overflow-hidden">
          <motion.span
            className="absolute inset-y-0 left-0 w-1/2 bg-[color:var(--te-primary)]"
            animate={{ x: ["-100%", "200%"] }}
            transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
          />
        </span>
      </motion.div>
    </section>
  );
}
